import React, { useEffect } from 'react';
import { SignupFormData, FormErrors, ValidationState, BusinessNameValidationState, NetworkSelectionState } from '../../types';

interface ReviewStepProps {
  formData: SignupFormData;
  errors: FormErrors;
  fcaValidation: ValidationState;
  businessNameValidation: BusinessNameValidationState;
  networkSelection: NetworkSelectionState;
  isSubmitting: boolean;
  submitError: string | null;
  updateField: (field: keyof SignupFormData, value: string | boolean) => void;
  onSubmit: () => void;
  onPrevious: () => void;
  canSubmit: boolean;
  onStepComplete?: (stepName: string) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  formData,
  errors,
  fcaValidation,
  businessNameValidation,
  networkSelection,
  isSubmitting,
  submitError,
  updateField,
  onSubmit,
  onPrevious,
  canSubmit,
  onStepComplete,
}) => {
  // Log all canSubmit dependencies whenever they change
  useEffect(() => {
    console.log('🔍 ReviewStep: canSubmit dependencies check', {
      canSubmit,
      isSubmitting,
      'networkSelection.canProceed': networkSelection.canProceed,
      'fcaValidation.isValid': fcaValidation.isValid,
      'businessNameValidation.isAvailable': businessNameValidation.isAvailable,
      'errors.length': Object.keys(errors).length,
      errors: errors,
      acceptTerms: formData.acceptTerms
    });
  }, [canSubmit, isSubmitting, networkSelection.canProceed, fcaValidation.isValid, businessNameValidation.isAvailable, errors, formData.acceptTerms]);

  const handleSubmit = () => {
    console.log('🎯 ReviewStep: Continue button clicked', {
      canSubmit,
      acceptTerms: formData.acceptTerms,
      willCallOnSubmit: canSubmit && formData.acceptTerms,
      // Log individual conditions
      isSubmitting,
      'networkSelection.canProceed': networkSelection.canProceed,
      'fcaValidation.isValid': fcaValidation.isValid,
      'businessNameValidation.isAvailable': businessNameValidation.isAvailable,
      'errors.length': Object.keys(errors).length,
      errors: errors
    });

    if (canSubmit && formData.acceptTerms) {
      console.log('✅ ReviewStep: Calling onSubmit handler');
      // Track final step completion in HubSpot
      if (onStepComplete) {
        onStepComplete('review');
      }
      onSubmit();
    } else {
      console.warn('⚠️ ReviewStep: Cannot submit', {
        canSubmit,
        acceptTerms: formData.acceptTerms,
        // Detailed breakdown of why submission is blocked
        blocking: {
          isSubmitting: isSubmitting ? '❌ Form is submitting' : '✅',
          networkCanProceed: !networkSelection.canProceed ? '❌ Network selection invalid' : '✅',
          fcaValid: !(fcaValidation.isValid === true || fcaValidation.isValid === null) ? '❌ FCA validation failed' : '✅',
          businessNameAvailable: businessNameValidation.isAvailable !== true ? '❌ Business name not available' : '✅',
          hasErrors: Object.keys(errors).length > 0 ? `❌ Has ${Object.keys(errors).length} errors` : '✅',
          acceptTerms: !formData.acceptTerms ? '❌ Terms not accepted' : '✅'
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          A quick agreement before we move forward
        </h1>
        <p className="text-gray-600">
          You're nearly there. Review these final details to finish setting up.
        </p>
      </div>

      {/* Terms and Conditions */}
      <div className="space-y-4">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="acceptTerms"
              name="acceptTerms"
              type="checkbox"
              checked={formData.acceptTerms}
              onChange={(e) => updateField('acceptTerms', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              required
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="acceptTerms" className="text-gray-700">
              I agree to the{' '}
              <button
                type="button"
                className="text-blue-600 underline hover:text-blue-700"
                onClick={() => window.open('https://www.smartr365.com/downloads/smartr365-terms-and-conditions-self-serve.pdf', '_blank')}
              >
                Terms and Conditions
              </button>
              {' '}and{' '}
              <button
                type="button"
                className="text-blue-600 underline hover:text-blue-700"
                onClick={() => window.open('https://www.smartr365.com/downloads/smartr365-privacy-policy-for-business-professionals.pdf', '_blank')}
              >
                Privacy Policy
              </button>
            </label>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="acceptMarketing"
              name="acceptMarketing"
              type="checkbox"
              checked={formData.acceptMarketing}
              onChange={(e) => updateField('acceptMarketing', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="acceptMarketing" className="text-gray-700">
              I'd like to hear about Smartr365's latest features and updates
            </label>
          </div>
        </div>
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Submission Error
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{submitError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onPrevious}
          disabled={isSubmitting}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!formData.acceptTerms || isSubmitting}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Setting up...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </div>
  );
};
